/**
 * Settings → Connections: every account a customer can plug in, in one place.
 *
 * It manages nothing itself and asserts nothing itself. Every status comes from the
 * server's readiness projection (`readiness` on `GET /v1/provider-keys?workspace_id=`),
 * read for the ONE workspace the customer has selected: whether a key is stored,
 * what its last check said, whether this workspace is allowed to use it, and which
 * operations can actually run. A key allowed in another workspace never makes this
 * one look ready, and a key that is stored but has no built operation says what is
 * planned instead of "Connected". The paired computer comes from `/v1/devices`.
 * Each row links to the section that manages it. No key material is read or shown.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useSearch } from 'wouter';

import { fetchDevices, type DeviceSummary } from '../../api/operations';
import { listWorkspaces, type Workspace } from '../../api/workspaces';
import { listProviderKeys } from '../../pages/ProviderKeys/providerKeysApi';
import { HelpTip } from '../HelpTip';
import { ConnectionSetupGuide, ProviderSetupGuide } from './ProviderSetupGuide';
import { SocialConnectPanel } from './SocialConnectPanel';
import {
  CONNECTION_CATALOG,
  CONNECTION_GROUPS,
  type CatalogEntry,
  type ConnectionGroup,
} from './connectionsCatalog';
import {
  readinessFrom,
  validationLabel,
  workspaceAuthLabel,
  type Readiness,
  type ReadinessProvider,
  type ReadinessStatus,
} from './connectionsReadiness';
import type { SettingsSection } from './settingsSections';
import { groupLight, readinessLight, type KeyLight } from './providerCapabilities';
import { deepLinkId } from '../../lib/unlockDeepLink';
import { ReturnToWorkspace } from './ReturnToWorkspace';

export interface ConnectionsState {
  /** The server projection for the selected workspace; null when it could not be read. */
  readiness: Readiness | null;
  workspaces: Workspace[];
  /** The workspace the statuses are for; null when the organisation has none. */
  activeWorkspace: string | null;
  computer: 'online' | 'offline' | 'none' | 'unknown';
}

export type Tone = 'on' | 'off' | 'pc' | 'soon' | 'warn';
type Status = { tone: Tone; label: string };

const LANE_LABELS: Record<string, string> = {
  connected: 'Signed in',
  signing_in: 'Signing in…',
  stale: 'Needs checking',
  unverifiable: 'Needs checking',
  expired: 'Signed out',
  not_connected: 'Not signed in',
};

/** Separate capabilities of one row, each shown with its own status (never merged). */
const CAPABILITY_LABELS: Record<string, string> = {
  analytics: 'Analytics (read-only)',
  upload: 'Upload',
  bridge_upload: 'Owner upload (Bridge)',
};

const UNAVAILABLE: Status = { tone: 'off', label: 'Status unavailable' };

function plannedTone(code: string): Tone {
  if (code === 'available_in_launch') return 'on';
  if (code === 'managed_social_connect') return 'warn';
  if (code === 'managed_social_not_configured') return 'off';
  if (code === 'desktop_bridge') return 'pc';
  if (code === 'not_available_hosted') return 'off';
  if (code === 'youtube_analytics_available') return 'warn';
  if (code === 'youtube_analytics_not_configured') return 'off';
  return 'soon';
}

export function providerOf(entry: CatalogEntry, state: ConnectionsState): ReadinessProvider | null {
  if (entry.kind !== 'key' || !state.readiness) return null;
  return state.readiness.providers.find((row) => row.id === entry.provider) || null;
}

export function plannedOf(entry: CatalogEntry, state: ConnectionsState): ReadinessStatus[] | null {
  if (!state.readiness) return null;
  const row = state.readiness.planned.find((item) => item.id === entry.id);
  return row ? row.statuses : null;
}

export function entryStatus(entry: CatalogEntry, state: ConnectionsState): Status {
  if (entry.kind === 'billing') return { tone: 'on', label: 'Managed in Billing' };
  if (!state.readiness) return UNAVAILABLE;
  if (entry.kind === 'key') {
    const provider = providerOf(entry, state);
    if (!provider) return UNAVAILABLE;
    if (!provider.stored) return { tone: 'off', label: 'No key stored' };
    const implemented = provider.operations.filter((op) => op.adapter === 'implemented');
    if (implemented.length === 0) {
      return { tone: 'soon', label: `Key stored · ${provider.planned[0]?.label || 'Not built yet in Auteur Cloud'}` };
    }
    const ready = implemented.filter((op) => op.operation_available);
    if (ready.length > 0) {
      const partial = ready.length < provider.operations.length
        ? ` (${ready.length} of ${provider.operations.length} operations)` : '';
      return { tone: 'on', label: `Ready in this workspace${partial}` };
    }
    const reason = implemented[0].unavailable?.label || 'Not available';
    return { tone: 'warn', label: `Key stored · ${reason}` };
  }
  if (entry.kind === 'subscription') {
    const lanes = state.readiness.lanes;
    if (!lanes) return UNAVAILABLE;
    const row = lanes.find((item) => item.lane === entry.lane);
    if (!row) return { tone: 'off', label: 'Not signed in' };
    return { tone: row.state === 'connected' ? 'on' : 'off', label: LANE_LABELS[row.state] || row.state };
  }
  const statuses = plannedOf(entry, state);
  if (!statuses || statuses.length === 0) return UNAVAILABLE;
  return { tone: plannedTone(statuses[0].code), label: statuses.map((item) => item.label).join(' · ') };
}

/**
 * F-27: the traffic light for a row: a key row's best key check, a subscription's sign-in.
 * Green means the provider accepted a key (or the sign-in is live), never "generation guaranteed".
 * Null when there is nothing to light (no key stored, a planned row).
 */
export function entryLight(entry: CatalogEntry, state: ConnectionsState, now: number = Date.now()): KeyLight | null {
  if (!state.readiness) return null;
  if (entry.kind === 'key') {
    const provider = providerOf(entry, state);
    if (!provider || !provider.stored) return null;
    // Per key, so a check that ages past 24 hours while the page is open turns yellow.
    const lights = provider.keys
      .map((key) => readinessLight(key.validation, key.checked_at, now))
      .filter((light): light is KeyLight => light !== null);
    const best = groupLight(lights);
    if (!best) return readinessLight(provider.validated, null, now);
    return { light: best.light, label: best.light === 'green' ? 'Connected' : best.light === 'red' ? 'Rejected / not working' : 'Not confirmed working' };
  }
  if (entry.kind === 'subscription') {
    const row = state.readiness.lanes?.find((item) => item.lane === entry.lane);
    if (!row) return null;
    if (row.state === 'connected') return { light: 'green', label: 'Signed in' };
    if (row.state === 'expired') return { light: 'red', label: 'Signed out' };
    if (row.state === 'not_connected') return null;
    return { light: 'yellow', label: LANE_LABELS[row.state] || 'Needs checking' };
  }
  return null;
}

export function LightBadge({ light }: { light: KeyLight }) {
  return (
    <span className={`status-light status-light-${light.light}`}>
      <span className="status-light-dot" aria-hidden="true" />
      <span className="status-light-label">{light.label}</span>
    </span>
  );
}

function howItConnects(entry: CatalogEntry): string {
  if (entry.kind === 'key') return 'API key, stored encrypted in your vault; Auteur Cloud makes each call for your workspace';
  if (entry.kind === 'subscription') return 'Your subscription, signed in on your paired computer';
  return entry.how;
}

function manageSection(entry: CatalogEntry): { section: SettingsSection; label: string } | null {
  if (entry.kind === 'key') return { section: 'keys', label: 'Manage keys' };
  if (entry.kind === 'subscription') return { section: 'subscriptions', label: 'Manage subscriptions' };
  if (entry.kind === 'roadmap' && entry.bridge) return { section: 'node', label: 'Pair your computer' };
  return null;
}

const COMPUTER_LABEL: Record<ConnectionsState['computer'], string> = {
  online: 'paired · online',
  offline: 'paired · offline',
  none: 'not paired',
  unknown: 'unknown',
};

function workspaceName(workspace: Workspace): string {
  return String(workspace.ingress_host || '').split('.')[0] || workspace.workspace_id;
}

function KeyDetail({ provider, readiness, now }: { provider: ReadinessProvider; readiness: Readiness; now: number }) {
  const checksOff = provider.verification === 'probe' && !readiness.validator.enabled;
  return (
    <>
      <dt>Keys</dt>
      <dd>
        {provider.keys.length === 0 ? 'No key stored.' : (
          <ul className="connections-keys">
            {provider.keys.map((key) => (
              <li key={key.key_id}>
                {(() => { const light = readinessLight(key.validation, key.checked_at, now); return light ? <><LightBadge light={light} />{' '}</> : null; })()}
                {key.label || 'Unlabelled key'} <code>{key.masked_hint}</code>
                {' · '}{validationLabel(key.validation, key.checked_at)}
                {' · '}{workspaceAuthLabel(key.authorized_for_workspace)}
              </li>
            ))}
          </ul>
        )}
      </dd>
      {checksOff && <><dt>Automatic checks</dt><dd>Off on this deployment, so keys show &quot;Not checked&quot;.</dd></>}
      {provider.verification !== 'probe' && (
        <><dt>Automatic checks</dt><dd>This provider has no free check; &quot;Cannot verify&quot; does not mean the key is wrong.</dd></>
      )}
      {provider.operations.length > 0 && (
        <>
          <dt>What can run</dt>
          <dd>
            <ul className="connections-operations">
              {provider.operations.map((op) => (
                <li key={op.operation}>
                  {op.operation}: {op.operation_available ? 'Available' : (op.planned?.label || op.unavailable?.label || 'Not available')}
                </li>
              ))}
            </ul>
          </dd>
        </>
      )}
      {provider.planned.map((item) => (
        <Fragment key={item.code}><dt>Planned</dt><dd>{item.label}{item.note ? ` ${item.note}` : ''}</dd></Fragment>
      ))}
    </>
  );
}

export function ConnectionsView({ state, onOpenSection, onSelectWorkspace, initialOpen = '' }: {
  state: ConnectionsState;
  onOpenSection?: (section: SettingsSection) => void;
  onSelectWorkspace?: (workspaceId: string) => void;
  /** A Connections row id from a workspace "How to unlock" link: opened and scrolled to. */
  initialOpen?: string;
}) {
  const known = CONNECTION_CATALOG.some((entry) => entry.id === initialOpen);
  // Lights age while the page stays open (a green check turns yellow after 24 hours).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const [open, setOpen] = useState<string>(known ? initialOpen : '');
  useEffect(() => {
    if (!known || typeof document === 'undefined') return;
    setOpen(initialOpen);
    const row = document.querySelector(`[data-entry="${initialOpen}"]`);
    if (row && typeof (row as HTMLElement).scrollIntoView === 'function') (row as HTMLElement).scrollIntoView({ block: 'center' });
  }, [initialOpen, known]);
  return (
    <div className="connections">
      <div className="connections-head">
        <p className="connections-intro">
          Accounts you already own, connected once and used across Auteur.{' '}
          <HelpTip topic="connections">
            Keys stay encrypted in your vault and are never shown again. Logins that live in a CLI,
            your browser or a desktop app stay on your own computer; Auteur sends it work and never sees
            the login. Every paid or public action still asks for your approval.
          </HelpTip>
        </p>
        {state.workspaces.length > 1 ? (
          <p className="connections-workspace">
            <label htmlFor="connections-workspace">Showing status for workspace </label>
            <select
              id="connections-workspace"
              value={state.activeWorkspace || ''}
              onChange={(event) => onSelectWorkspace?.(event.target.value)}
            >
              {state.workspaces.map((workspace) => (
                <option key={workspace.workspace_id} value={workspace.workspace_id}>{workspaceName(workspace)}</option>
              ))}
            </select>
          </p>
        ) : (
          <p className="connections-workspace">
            {state.workspaces.length === 1
              ? <>Showing status for workspace <strong>{workspaceName(state.workspaces[0])}</strong></>
              : 'No workspace yet: keys can be stored, but nothing runs until a workspace is allowed to use them.'}
          </p>
        )}
        <p className="connections-computer">
          <span className={`connections-dot connections-dot-${state.computer}`} aria-hidden="true" />
          Your computer: <strong>{COMPUTER_LABEL[state.computer]}</strong>
        </p>
      </div>
      {CONNECTION_GROUPS.map((group) => (
        <section key={group.id} className={`connections-group connections-group-${group.id}`} aria-labelledby={`cx-${group.id}`}>
          <h3 id={`cx-${group.id}`}>
            {group.label} <HelpTip topic={group.label.toLowerCase()}>{group.help}</HelpTip>
          </h3>
          <ul className="connections-list">
            {CONNECTION_CATALOG.filter((entry) => entry.group === group.id).map((entry) => {
              const status = entryStatus(entry, state);
              const manage = manageSection(entry);
              const expanded = open === entry.id;
              const light = entryLight(entry, state, now);
              const provider = providerOf(entry, state);
              const planned = entry.kind === 'roadmap' ? plannedOf(entry, state) : null;
              return (
                <li key={entry.id} className="connections-row" data-entry={entry.id}>
                  <button
                    type="button"
                    className="connections-row-button"
                    aria-expanded={expanded}
                    aria-controls={`cx-detail-${entry.id}`}
                    onClick={() => setOpen(expanded ? '' : entry.id)}
                  >
                    <span className="connections-name">{entry.name}{light ? <>{' '}<LightBadge light={light} /></> : null}</span>
                    <span className="connections-used">Used in: {entry.usedIn}</span>
                    <span className={`connections-status connections-status-${status.tone}`}>{status.label}</span>
                  </button>
                  {expanded && (
                    <div className="connections-detail" id={`cx-detail-${entry.id}`}>
                      <dl>
                        <dt>How it connects</dt><dd>{howItConnects(entry)}</dd>
                        <dt>Status</dt><dd>{status.label}</dd>
                        {provider && state.readiness && <KeyDetail provider={provider} readiness={state.readiness} now={now} />}
                        {planned && planned.map((item) => (item.capability ? (
                          <Fragment key={item.code}>
                            <dt>{CAPABILITY_LABELS[item.capability] || item.capability}</dt>
                            <dd>{item.label}{item.note ? ` ${item.note}` : ''}</dd>
                          </Fragment>
                        ) : item.note ? (
                          <Fragment key={item.code}><dt>Note</dt><dd>{item.note}</dd></Fragment>
                        ) : null))}
                        {entry.kind === 'roadmap' && entry.bridge && state.computer === 'none' && (
                          <><dt>Next step</dt><dd>Pair your computer now, so this is ready the day it arrives.</dd></>
                        )}
                        {entry.kind === 'roadmap' && entry.launch && (
                          <><dt>Where</dt><dd>Open your workspace, then Launch → Commerce &amp; Forms. This is not how you pay for Auteur; that is <Link to="/billing">Billing</Link>.</dd></>
                        )}
                        {entry.kind === 'key' && provider?.stored && provider.authorized_for_workspace === false
                          && provider.operations.some((op) => op.adapter === 'implemented') && (
                          <><dt>Next step</dt><dd>Turn on "Allow my workspace to use this key for chat and generation" beside the key.</dd></>
                        )}
                      </dl>
                      {entry.kind === 'key' && (
                        <ProviderSetupGuide providerId={entry.provider} context="connections" verification={provider?.verification ?? null} />
                      )}
                      {entry.id === 'socials' && <SocialConnectPanel />}
                      {(entry.kind === 'roadmap' || entry.kind === 'subscription') && <ConnectionSetupGuide entryId={entry.id} />}
                      {entry.kind === 'billing' && <Link to={entry.href}>Open Billing (pay for Auteur)</Link>}
                      {manage && onOpenSection && (
                        <button type="button" className="secondary" onClick={() => onOpenSection(manage.section)}>
                          {manage.label}
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

const NODE_ONLINE_WINDOW_MS = 90 * 1000;

function computerState(devices: DeviceSummary[], now: number): ConnectionsState['computer'] {
  const active = devices.filter((device) => device.status === 'active');
  if (active.length === 0) return 'none';
  const online = active.some((device) => device.online === true || device.connection_state === 'connected'
    || (device.last_seen_at ? now - Date.parse(device.last_seen_at) < NODE_ONLINE_WINDOW_MS : false));
  return online ? 'online' : 'offline';
}

/**
 * Load everything the view needs for one workspace. Always a fresh read: there is no
 * cached "connected", so a key deleted or an approval revoked elsewhere shows at once.
 */
export async function loadConnectionsState(
  requested: string | null,
  deps = { listProviderKeys, listWorkspaces, fetchDevices },
): Promise<ConnectionsState> {
  const [workspacesResult, devicesResult] = await Promise.allSettled([deps.listWorkspaces(), deps.fetchDevices()]);
  const workspaces = workspacesResult.status === 'fulfilled' ? workspacesResult.value : [];
  const activeWorkspace = workspaces.some((item) => item.workspace_id === requested)
    ? requested
    : (workspaces[0]?.workspace_id ?? null);
  let readiness: Readiness | null = null;
  try {
    readiness = readinessFrom(await deps.listProviderKeys(activeWorkspace));
  } catch {
    readiness = null;
  }
  return {
    readiness,
    workspaces,
    activeWorkspace,
    computer: devicesResult.status === 'fulfilled'
      ? computerState(devicesResult.value.devices || [], Date.now()) : 'unknown',
  };
}

export function ConnectionsPanel({ onOpenSection }: { onOpenSection?: (section: SettingsSection) => void }) {
  const search = useSearch();
  const [state, setState] = useState<ConnectionsState>({
    readiness: null, workspaces: [], activeWorkspace: null, computer: 'unknown',
  });

  const load = useCallback(async (workspaceId: string | null) => {
    setState(await loadConnectionsState(workspaceId));
  }, []);

  useEffect(() => { void load(null); }, [load]);

  return (
    <section className="settings-panel-content">
      <h3>Connections</h3>
      <ReturnToWorkspace search={search} />
      <ConnectionsView
        state={state}
        onOpenSection={onOpenSection}
        onSelectWorkspace={(id) => void load(id)}
        initialOpen={deepLinkId(search, 'connection')}
      />
    </section>
  );
}

export type { ConnectionGroup };
